/* eslint-disable max-lines */
/* eslint sort-keys: ["error"] */
import { z } from 'zod';
import { FisDataTimingEnum } from '../../../../db';
import { workOrderCfgSchema, workOrderSocketCfg } from './nodes/work-order.node-cfg';
import {
    type BaseNodeCfg,
    type NodeDataSocketKind,
    type NodeKind,
    type NodeSocketCfg,
    type NodeSocketDef,
    type NodeSocketKind,
    baseNodeCfgSchema,
} from './nodes/base-node';
import { workInstructionNodeCfgSchema, workInstructionNodeSocketCfg } from './nodes/work-instruction.zod';
import { materialAssemblyNodeCfg, materialAssemblyNodeCfgSchema } from './nodes/material-assembly.zod';
import { materialComparisonNodeCfg, materialComparisonNodeCfgSchema } from './nodes/material-comparison.zod';
import { materialDisassemblyNodeCfg, materialDisassemblyNodeCfgSchema } from './nodes/material-disassembly.zod';
import { processCollectNodeCfgSchema, processCollectNodeSocketCfg } from './nodes/process-collect.zod';
import { workOrderSetStatusCfgSchema, workOrderSetStatusSocketCfg } from './nodes/work-order-set-status.zod';
import { updateMaterialStatusCfgSchema, updateMaterialStatusSocketCfg } from './nodes/update-material-status.zod';
import { goToTabNodeCfgSchema, goToTabNodeSocketCfg } from './nodes/go-to-tab.zod';
import { nextTabNodeCfgSchema, nextTabNodeSocketCfg } from './nodes/next-tab.zod';
import { previousTabNodeCfgSchema, previousTabNodeSocketCfg } from './nodes/previous-tab.zod';
import { assetSchema } from '../asset.deployParam';
import { materialLookupNodeCfgSchema, materialLookupNodeSocketCfg } from './nodes/material-lookup.node-cfg';
import { materialSerialNumberNodeCfgSchema, materialSerialNumberNodeSocketCfg } from './nodes/material-serial-number.node-cfg';
import { functionalSelectNodeCfgSchema, functionalSelectNodeSocketCfg } from './nodes/functional-select.zod';
import { functionalInputsNodeCfgSchema, functionalInputsNodeSocketCfg } from './nodes/functional-inputs.zod';
import { functionalOutputsNodeCfgSchema, functionalOutputsNodeSocketCfg } from './nodes/functional-outputs.zod';
import { booleanComparisonNodeCfg, booleanComparisonNodeCfgSchema } from './nodes/boolean-comparison.zod';
import { activatePickLightCfgSchema, activatePickLightSocketCfg } from './nodes/activate-pick-light.zod';
import { pickLightChannelCfgSchema, pickLightChannelSocketCfg } from './nodes/pick-light-channel.zod';
import { tcpConnectionCfgSchema, tcpConnectionSocketCfg } from './nodes/tcp-connection.zod';
import { mqttPublishNodeCfgSchema, mqttPublishNodeSocketCfg } from './nodes/mqtt-publish.zod';
import { mqttSubscribeNodeCfgSchema, mqttSubscribeNodeSocketCfg } from './nodes/mqtt-subscribe.zod';
import { sparkplugCommanderNodeCfgSchema, sparkplugCommanderNodeSocketCfg } from './nodes/sparkplug-commander.zod';
import { sparkplugConsumerNodeCfgSchema, sparkplugConsumerNodeSocketCfg } from './nodes/sparkplug-consumer.zod';
import { ioLinkNodeCfgSchema, ioLinkNodeSocketCfg } from './nodes/io-link.zod';
import { isduReadNodeCfgSchema, isduReadNodeSocketCfg } from './nodes/isdu-read.zod';
import { isduWriteNodeCfgSchema, isduWriteNodeSocketCfg } from './nodes/isdu-write.zod';
import { timestampNodeCfgSchema, timestampNodeSocketCfg } from './nodes/timestamp.zod';
import { fileReadNodeCfg, fileReadNodeCfgSchema } from './nodes/file-read.zod';
import { fileWriteNodeCfg, fileWriteNodeCfgSchema } from './nodes/file-write.zod';
import { addMaterialNoteCfgSchema, addMaterialNoteNodeSocketCfg } from './nodes/add-material-note.zod';
import { udpConnectionCfgSchema, udpConnectionSocketCfg } from './nodes/udp-connection.zod';
import { openProtocolConnectionCfgSchema, openProtocolConnectionSocketCfg } from './nodes/open-protocol-connection.zod';
import { torqueToolCommanderNodeCfgSchema, torqueToolCommanderNodeSocketCfg } from './nodes/torque-tool-commander.zod';
import { deactivatePickLightCfgSchema, deactivatePickLightSocketCfg } from './nodes/deactivate-pick-light.zod';
import { processSolveNodeCfgSchema, processSolveNodeSocketCfg } from './nodes/process-solve.zod';
import { torqueToolConsumerNodeCfgSchema, torqueToolConsumerNodeSocketCfg } from './nodes/torque-tool-consumer.zod';
import { processStatusNodeCfgSchema, processStatusNodeSocketCfg } from './nodes/process-status.node-cfg';
import { workInstructionStatusNodeCfgSchema, workInstructionStatusNodeSocketCfg } from './nodes/work-instruction-status.node-cfg';
import { getActiveTabNodeCfgSchema, getActiveTabNodeSocketCfg } from './nodes/get-active-tab.zod';
import { createMaterialNodeCfgSchema, createMaterialNodeSocketCfg } from './nodes/create-material.zod';
import { resetVariablesNodeCfgSchema, resetVariablesNodeSocketCfg } from './nodes/reset-variables.zod';
import { importMaterialNodeCfgSchema, importMaterialNodeSocketCfg } from './nodes/import-material.zod';

export * from './nodes/base-node';

export const nodeCfgSchemaMap = {
    activatePickLight: activatePickLightCfgSchema,
    activeOperator: baseNodeCfgSchema.extend({
        kind: z.literal('activeOperator'),
    }),
    addMaterialNote: addMaterialNoteCfgSchema,
    appletEventEmit: baseNodeCfgSchema.extend({
        controlValues: z.object({
            appletEventId: z.string(),
        }),
        kind: z.literal('appletEventEmit'),
    }),
    appletEventListen: baseNodeCfgSchema.extend({
        controlValues: z.object({
            appletEventId: z.string(),
        }),
        kind: z.literal('appletEventListen'),
    }),
    attributeMaterialLink: baseNodeCfgSchema.extend({
        controlValues: z.object({
            attributeId: z.string(),
        }),
        kind: z.literal('attributeMaterialLink'),
    }),
    boolCast: baseNodeCfgSchema.extend({
        kind: z.literal('boolCast'),
    }),
    boolean: baseNodeCfgSchema.extend({
        controlValues: z.object({
            value: z.boolean(),
        }),
        kind: z.literal('boolean'),
    }),
    booleanComparison: booleanComparisonNodeCfgSchema,
    comparisonNumber: baseNodeCfgSchema.extend({
        controlValues: z.object({
            not: z.boolean(),
            operation: z.enum([ '=', '>', '>=', '<', '<=' ]),
        }),
        kind: z.literal('comparisonNumber'),
    }),
    comparisonString: baseNodeCfgSchema.extend({
        controlValues: z.object({
            not: z.boolean(),
            operation: z.enum([ '=', 'contains', 'startsWith', 'endsWith' ]),
        }),
        kind: z.literal('comparisonString'),
    }),
    createMaterial: createMaterialNodeCfgSchema,
    deactivatePickLight: deactivatePickLightCfgSchema,
    defaultVal: baseNodeCfgSchema.extend({
        kind: z.literal('defaultVal'),
    }),
    eventDelay: baseNodeCfgSchema.extend({
        controlValues: z.object({
            delayMs: z.number(),
        }),
        kind: z.literal('eventDelay'),
    }),
    eventOneShot: baseNodeCfgSchema.extend({
        kind: z.literal('eventOneShot'),
    }),
    eventSwitch: baseNodeCfgSchema.extend({
        controlValues: z.object({
            conditionInputs: z.number(),
            logicalOperator: z.enum([ 'and', 'or' ]),
        }).default({
            conditionInputs: 1,
            logicalOperator: 'and',
        }),
        kind: z.literal('eventSwitch'),
    }),
    eventThrottle: baseNodeCfgSchema.extend({
        controlValues: z.object({
            max: z.number(),
            windowMs: z.number(),
        }),
        kind: z.literal('eventThrottle'),
    }),
    fileRead: fileReadNodeCfgSchema,
    fileWrite: fileWriteNodeCfgSchema,
    flowError: baseNodeCfgSchema.extend({
        kind: z.literal('flowError'),
    }),
    functionalInputs: functionalInputsNodeCfgSchema,
    functionalOutputs: functionalOutputsNodeCfgSchema,
    functionalSelect: functionalSelectNodeCfgSchema,
    getActiveTab: getActiveTabNodeCfgSchema,
    goToTab: goToTabNodeCfgSchema,
    importMaterial: importMaterialNodeCfgSchema,
    ioLink: ioLinkNodeCfgSchema,
    isduRead: isduReadNodeCfgSchema,
    isduWrite: isduWriteNodeCfgSchema,
    jsonAgg: baseNodeCfgSchema.extend({
        controlValues: z.object({
            outputExpressions: z.array(z.string().regex(/^[0-9a-z]+$/ui, 'Only simple identifiers are allowed in output expressions')),
        }),
        kind: z.literal('jsonAgg'),
    }),
    jsonParse: baseNodeCfgSchema.extend({
        controlValues: z.object({
            outputExpressions: z.array(z.string().regex(/^[0-9a-z]+$/ui, 'Only simple identifiers are allowed in output expressions')),
        }),
        kind: z.literal('jsonParse'),
    }),
    logOutOperator: baseNodeCfgSchema.extend({
        kind: z.literal('logOutOperator'),
    }),
    materialAssembly: materialAssemblyNodeCfgSchema,
    materialComparison: materialComparisonNodeCfgSchema,
    materialDisassembly: materialDisassemblyNodeCfgSchema,
    materialLookup: materialLookupNodeCfgSchema,
    materialSerialNumber: materialSerialNumberNodeCfgSchema,
    mqttPublish: mqttPublishNodeCfgSchema,
    mqttSubscribe: mqttSubscribeNodeCfgSchema,
    nextTab: nextTabNodeCfgSchema,
    not: baseNodeCfgSchema.extend({
        kind: z.literal('not'),
    }),
    numCast: baseNodeCfgSchema.extend({
        kind: z.literal('numCast'),
    }),
    numClamp: baseNodeCfgSchema.extend({
        controlValues: z.object({
            max: z.number().optional(),
            min: z.number().optional(),
        }),
        kind: z.literal('numClamp'),
    }),
    number: baseNodeCfgSchema.extend({
        controlValues: z.object({
            value: z.number(),
        }),
        kind: z.literal('number'),
    }),
    openProtocolConnection: openProtocolConnectionCfgSchema,
    performanceDataCollect: baseNodeCfgSchema.extend({
        controlValues: z.object({
            assetCfg: assetSchema,
            dataLabelId: z.string(),
            timing: z.literal(FisDataTimingEnum.Start).catch(FisDataTimingEnum.Start), // we're not sure if we want to expose the End timing yet
        }),
        kind: z.literal('performanceDataCollect'),
    }),
    pickLightChannel: pickLightChannelCfgSchema,
    previousTab: previousTabNodeCfgSchema,
    processCollect: processCollectNodeCfgSchema,
    processSolve: processSolveNodeCfgSchema,
    processStatus: processStatusNodeCfgSchema,
    resetVariables: resetVariablesNodeCfgSchema,
    script: baseNodeCfgSchema.extend({
        controlValues: z.object({
            scriptId: z.string(),
        }),
        kind: z.literal('script'),
    }),
    slice: baseNodeCfgSchema.extend({
        kind: z.literal('slice'),
    }),
    sparkplugCommander: sparkplugCommanderNodeCfgSchema,
    sparkplugConsumer: sparkplugConsumerNodeCfgSchema,
    start: baseNodeCfgSchema.extend({
        kind: z.literal('start'),
    }),
    strCast: baseNodeCfgSchema.extend({
        kind: z.literal('strCast'),
    }),
    tcpConnection: tcpConnectionCfgSchema,
    text: baseNodeCfgSchema.extend({
        controlValues: z.object({
            value: z.string(),
        }),
        kind: z.literal('text'),
    }),
    timer: baseNodeCfgSchema.extend({
        controlValues: z.object({
            dir: z.union([ z.literal('up'), z.literal('down') ]),
            /** the number the timer should stop at */
            limit: z.number(),
        }),
        kind: z.literal('timer'),
    }),
    timestamp: timestampNodeCfgSchema,
    torqueToolCommander: torqueToolCommanderNodeCfgSchema,
    torqueToolConsumer: torqueToolConsumerNodeCfgSchema,
    txtFormat: baseNodeCfgSchema.extend({
        controlValues: z.object({
            formatTemplate: z.string(),
            inputs: z.number(),
        }),
        kind: z.literal('txtFormat'),
    }),
    udpConnection: udpConnectionCfgSchema,
    updateMaterialStatus: updateMaterialStatusCfgSchema,
    varAssigned: baseNodeCfgSchema.extend({
        controlValues: z.object({
            changesOnly: z.boolean(),
            varId: z.string(),
        }),
        kind: z.literal('varAssigned'),
    }),
    varRead: baseNodeCfgSchema.extend({
        controlValues: z.object({
            varId: z.string(),
        }),
        kind: z.literal('varRead'),
    }),
    varWrite: baseNodeCfgSchema.extend({
        controlValues: z.object({
            varId: z.string(),
        }),
        kind: z.literal('varWrite'),
    }),
    varWriteMultiple: baseNodeCfgSchema.extend({
        controlValues: z.object({
            /** FUTURE: Rename to variable ids */
            vars: z.array(z.string()),
        }),
        kind: z.literal('varWriteMultiple'),
    }),
    workInstruction: workInstructionNodeCfgSchema,
    workInstructionStatus: workInstructionStatusNodeCfgSchema,
    workOrder: workOrderCfgSchema,
    workOrderSetStatus: workOrderSetStatusCfgSchema,
} satisfies {
    // ensure all node schemas don't override anything from baseNodeCfgSchema, and all node kinds are covered.
    [nodeKind in NodeKind]: z.ZodType<BaseNodeCfg & { kind: NodeKind }>;
};

export const nodeCfgSchema: z.ZodType<BaseNodeCfg> = baseNodeCfgSchema.transform((val, ctx) => {
    // this will check the specific schema for the node kind.
    // by not using discriminated union, we can easily ensure that all node kinds are covered,
    // just by propagating the issues up.

    // also we can validate that the socket overrides are valid.

    const specificSchema = nodeCfgSchemaMap[val.kind];
    if (!val.disabled) {
        if (val.kind === 'performanceDataCollect' && val.socketDefaults?.inputs?.assetId && !val.controlValues?.assetCfg) {
            // this is a breaking change that we originally thought we could ignore, but it didn't work out
            const {
                assetId,
                ...newSocketDefaultInputs
            } = val.socketDefaults.inputs;
            val = {
                ...val,
                controlValues: {
                    ...val.controlValues,
                    assetCfg: {
                        id: assetId,
                    },
                },
                socketDefaults: {
                    ...val.socketDefaults,
                    inputs: newSocketDefaultInputs,
                },
            };
        }
        
        const parseResult = specificSchema.safeParse(val, {
            refinementCtxExtra: ctx.extra,
        });
        if (!parseResult.success) {
            const nodeTitle = val.label ? `${val.kind} node "${val.label}"` : `${val.kind} node`;
            for (const issue of parseResult.error.issues) {
                ctx.addIssue({
                    ...issue,
                    fatal: true,
                    message: `${nodeTitle} - ${issue.message}`,
                });
            }
            return z.NEVER;
        }
        return parseResult.data as BaseNodeCfg;
    }

    // TODO validate socketDefaults
    return val as BaseNodeCfg;
});

export type NodeCfg<KIND extends NodeKind> = z.infer<typeof nodeCfgSchemaMap[KIND]>;

/**
 * a mapping from node kind to its sockets
 * @note it should be assumed that the value of every input is nullable
 * @TODO this could be more concise, I'm not sure we need to separate the control and data socket objects here, because we can identify the control sockets with the `event` kind.
 */
export const nodeSocketKindMap = {
    activatePickLight: activatePickLightSocketCfg,
    activeOperator: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ loggedIn: 'event', loggedOut: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({
                firstName: 'string',
                lastName: 'string',
                username: 'string',
            }),
        },
    },
    addMaterialNote: addMaterialNoteNodeSocketCfg,
    appletEventEmit: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    appletEventListen: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ event: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    attributeMaterialLink: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({ materialId: 'string' }),
            outputs: () => ({}),
        },
    },
    boolCast: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'varValue' }),
            outputs: () => ({ output: 'boolean' }),
        },
    },
    boolean: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({ output: 'boolean' }),
        },
    },
    booleanComparison: booleanComparisonNodeCfg,
    comparisonNumber: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input1: 'number', input2: 'number' }),
            outputs: () => ({ output: 'boolean' }),
        },
    },
    comparisonString: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input1: 'string', input2: 'string' }),
            outputs: () => ({ output: 'boolean' }),
        },
    },
    createMaterial: createMaterialNodeSocketCfg,
    deactivatePickLight: deactivatePickLightSocketCfg,
    defaultVal: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ defaultValue: 'varValue', input: 'varValue' }),
            outputs: () => ({ output: 'varValue' }),
        },
    },
    eventDelay: {
        controlSockets: {
            inputs: () => ({ input: 'event' }),
            outputs: () => ({ output: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    eventOneShot: {
        controlSockets: {
            inputs: () => ({ input: 'event', reset: 'event' }),
            outputs: () => ({ output: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    eventSwitch: {
        controlSockets: {
            inputs: () => ({ event: 'event' }),
            outputs: () => ({ false: 'event', true: 'event' }),
        },
        dataSockets: {
            inputs: (cfg: NodeCfg<'eventSwitch'>): Record<'condition' | `condition${number}`, 'varValue'> => {
                const inputs: Record<'condition' | `condition${number}`, 'varValue'> = {
                    condition: 'varValue',
                };
                for (let i = 1; i < cfg.controlValues.conditionInputs + 1; i++) {
                    inputs[`condition${i}`] = 'varValue';
                }
                return inputs;
            },
            outputs: () => ({}),
        },
    },
    eventThrottle: {
        controlSockets: {
            inputs: () => ({ input: 'event' }),
            outputs: () => ({ output: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    fileRead: fileReadNodeCfg,
    fileWrite: fileWriteNodeCfg,
    flowError: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ trigger: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({ error: 'string' }),
        },
    },
    functionalInputs: functionalInputsNodeSocketCfg,
    functionalOutputs: functionalOutputsNodeSocketCfg,
    functionalSelect: functionalSelectNodeSocketCfg,
    getActiveTab: getActiveTabNodeSocketCfg,
    goToTab: goToTabNodeSocketCfg,
    importMaterial: importMaterialNodeSocketCfg,
    ioLink: ioLinkNodeSocketCfg,
    isduRead: isduReadNodeSocketCfg,
    isduWrite: isduWriteNodeSocketCfg,
    jsonAgg: {
        controlSockets: {
            inputs: () => ({
                reset: 'event',
                trigger: 'event',
            }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({ input: 'string' }),
            outputs: (cfg: NodeCfg<'jsonAgg'>): {
                merged: 'string',
                [ key: `output${number}` ]: 'varValue';
            } => {
                const outputs: {
                    merged: 'string',
                    [ key: `output${number}` ]: 'varValue';
                } = {
                    merged: 'string',
                };
                for (let i = 0; i < cfg.controlValues.outputExpressions.length; i++) {
                    outputs[`output${i}`] = 'varValue';
                }
                return outputs;
            },
        },
    },
    jsonParse: {
        controlSockets: {
            inputs: () => ({
                trigger: 'event',
            }),
            outputs: () => ({
                next: 'event',
            }),
        },
        dataSockets: {
            inputs: () => ({ input: 'string' }),
            outputs: (cfg: NodeCfg<'jsonParse'>): Record<`output${number}`, 'varValue'> => {
                const outputEntries = cfg.controlValues.outputExpressions.map((_, i) => [ `output${i}`, 'varValue' ]);
                return Object.fromEntries(outputEntries);
            },
        },
    },
    logOutOperator: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    materialAssembly: materialAssemblyNodeCfg,
    materialComparison: materialComparisonNodeCfg,
    materialDisassembly: materialDisassemblyNodeCfg,
    materialLookup: materialLookupNodeSocketCfg,
    materialSerialNumber: materialSerialNumberNodeSocketCfg,
    mqttPublish: mqttPublishNodeSocketCfg,
    mqttSubscribe: mqttSubscribeNodeSocketCfg,
    nextTab: nextTabNodeSocketCfg,
    not: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'boolean' }),
            outputs: () => ({ output: 'boolean' }),
        },
    },
    numCast: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'varValue' }),
            outputs: () => ({ output: 'number' }),
        },
    },
    numClamp: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'number' }),
            outputs: () => ({ output: 'number' }),
        },
    },
    number: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({ output: 'number' }),
        },
    },
    openProtocolConnection: openProtocolConnectionSocketCfg,
    performanceDataCollect: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({ numericValue: 'number' }),
            outputs: () => ({}),
        },
    },
    pickLightChannel: pickLightChannelSocketCfg,
    previousTab: previousTabNodeSocketCfg,
    processCollect: processCollectNodeSocketCfg,
    processSolve: processSolveNodeSocketCfg,
    processStatus: processStatusNodeSocketCfg,
    resetVariables: resetVariablesNodeSocketCfg,
    script: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    slice: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ end: 'number', input: 'string', start: 'number' }),
            outputs: () => ({ output: 'string' }),
        },
    },
    sparkplugCommander: sparkplugCommanderNodeSocketCfg,
    sparkplugConsumer: sparkplugConsumerNodeSocketCfg,
    start: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    strCast: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'anyData' }),
            outputs: () => ({ output: 'string' }),
        },
    },
    tcpConnection: tcpConnectionSocketCfg,
    text: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({ output: 'string' }),
        },
    },
    timer: {
        controlSockets: {
            inputs: () => ({ reset: 'event', start: 'event', stop: 'event' }),
            outputs: () => ({ done: 'event', tick: 'event' }),
        },
        dataSockets: {
            inputs: () => ({ initialValue: 'number' }),
            outputs: () => ({ value: 'number' }),
        },
    },
    timestamp: timestampNodeSocketCfg,
    torqueToolCommander: torqueToolCommanderNodeSocketCfg,
    torqueToolConsumer: torqueToolConsumerNodeSocketCfg,
    txtFormat: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: (cfg: NodeCfg<'txtFormat'>): Record<`input${number}`, 'varValue'> => {
                const inputEntries: [`input${number}`, 'varValue'][] = [];
                for (let i = 0; i < cfg.controlValues.inputs; i++) {
                    inputEntries.push([ `input${i}`, 'varValue' ]);
                }
                return Object.fromEntries(inputEntries);
            },
            outputs: () => ({ output: 'string' }),
        },
    },
    udpConnection: udpConnectionSocketCfg,
    updateMaterialStatus: updateMaterialStatusSocketCfg,
    varAssigned: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ trigger: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({ newValue: 'varValue' as NodeDataSocketKind, oldValue: 'varValue' as NodeDataSocketKind }),
        },
    },
    varRead: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: (): { value: NodeDataSocketKind } => ({
                value: 'varValue' as NodeDataSocketKind,
            }),
        },
    },
    varWrite: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: (): { value: 'string' | 'number' | 'boolean' | 'varValue' } => ({
                value: 'varValue',
            }),
            outputs: () => ({}),
        },
    },
    varWriteMultiple: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: (cfg: NodeCfg<'varWriteMultiple'>): Record<`var${number}`, 'string' | 'boolean' | 'number' | 'varValue'> => {
                const inputEntries: [`var${number}`, 'varValue'][] = [];
                for (let i = 0; i < cfg.controlValues.vars.length; i++) {
                    inputEntries.push([ `var${i}`, 'varValue' ]);
                }
                return Object.fromEntries(inputEntries);
            },
            outputs: () => ({}),
        },
    },
    workInstruction: workInstructionNodeSocketCfg,
    workInstructionStatus: workInstructionStatusNodeSocketCfg,
    workOrder: workOrderSocketCfg,
    workOrderSetStatus: workOrderSetStatusSocketCfg,
} satisfies {
    [kind in NodeKind]: NodeSocketCfg<NodeCfg<kind>>;
};

export const compatibilityMatrix: Record<NodeSocketKind, NodeSocketKind[]> = {
    anyData: [ 'anyData' ],
    boolean: [ 'anyData', 'boolean', 'varValue' ],
    event: [ 'event' ],
    number: [ 'anyData', 'number', 'varValue' ],
    string: [ 'anyData', 'string', 'varValue' ],
    varValue: [ 'anyData', 'varValue' ],
    workOrderMaterialData: [ 'anyData', 'workOrderMaterialData' ],
};

export function areSocketsCompatible (a: NodeSocketKind, b: NodeSocketKind): boolean {
    return compatibilityMatrix[a].includes(b);
}

export function getNodeSocketMaps<KIND extends NodeKind> (cfg: NodeCfg<KIND>) {
    const dataSocketMapItem = nodeSocketKindMap[cfg.kind].dataSockets as NodeSocketDef<NodeCfg<KIND>, NodeSocketKind>;
    const controlSocketMapItem = nodeSocketKindMap[cfg.kind].controlSockets as NodeSocketDef<NodeCfg<KIND>, NodeSocketKind>;
    return {
        inputs: {
            control: controlSocketMapItem.inputs(cfg),
            data: dataSocketMapItem.inputs(cfg),
        },
        outputs: {
            control: controlSocketMapItem.outputs(cfg),
            data: dataSocketMapItem.outputs(cfg),
        },
    };
}
